import isEqual from 'lodash/isEqual';
import {
  CardDiscountRange,
  IMergedPromotions,
  IPromotion,
  PaymentMethod,
  PromotionType,
  Unit,
  convertPromotionMapToArray,
  defaultNaverPayPromotion,
  getDiscountStr,
  getNPayBenefits,
  getNewDiscountInfo,
  groupByPaymentMethod,
  isCombinedBenefit,
  isPromotionTarget,
  suffixMap,
} from 'common/promotion';
import { IPassengers } from 'contexts/searchParams';
import { IFare } from '../types';
import { DomesticItinerary } from 'hooks/useDomesticFlights';

const descriptionMap = new Map([
  [CardDiscountRange.ONLY_BASIC_FARE, '기본항공운임 대상'],
  [CardDiscountRange.EXCLUDE_PUBLISH_FEE, '제휴사 발권수수료 제외'],
  [CardDiscountRange.TOTAL_FARE, '항공운임 총액 대상'],
]);

const getTotalFare = (
  { adultFare, childFare, aFuel, cFuel, aTax, cTax, publishFee }: IFare,
  passengers: IPassengers,
) =>
  (adultFare + aFuel + aTax + publishFee) * passengers.adult +
  (childFare + cFuel + cTax + publishFee) * passengers.child;

const getNPayPromotions = (
  promotions: IPromotion[],
  flights: DomesticItinerary[],
  otaId: string,
  supportNPay: boolean,
  fares: IFare[],
  { adult, child, infant }: IPassengers,
) => {
  function callbackfn(acc: IMergedPromotions, promotion: IPromotion) {
    const passengerCount = {
      adult: promotion.passengers.adult ? adult : 0,
      child: promotion.passengers.child ? child : 0,
      infant: promotion.passengers.infant ? infant : 0,
    };
    const { totalDiscount, partial } = flights.reduce(
      (acc, flight, index) => {
        if (
          isPromotionTarget(promotion, {
            airlineCode: flight.segment.airlineCode,
            departureDate:
              flight.segment.departure.date + flight.segment.departure.time,
            otaId,
            supportNPay,
            departureAirport: flight.segment.departure.airportCode,
            arrivalAirport: flight.segment.arrival.airportCode,
          })
        ) {
          acc.totalDiscount += getNPayBenefits(
            promotion.benefit,
            getTotalFare(fares[index], passengerCount),
            passengerCount,
          );
        } else {
          acc.partial = true;
        }
        return acc;
      },
      { totalDiscount: 0, partial: false },
    );
    const { paymentMethods, benefit, description } = promotion;
    if (!totalDiscount) return acc;
    if (description) acc.descriptions.push({ description, totalDiscount });
    if (!paymentMethods) {
      groupByPaymentMethod(
        acc.promotionMap,
        undefined,
        PromotionType.NAVER_PAY,
        benefit,
        totalDiscount,
        partial,
      );
      return acc;
    }
    paymentMethods.forEach(paymentMethod => {
      if (paymentMethod.code === PaymentMethod.NAVER_PAY && !supportNPay) {
        return;
      }
      groupByPaymentMethod(
        acc.promotionMap,
        paymentMethod,
        PromotionType.NAVER_PAY,
        benefit,
        totalDiscount,
        partial,
      );
    });
    return acc;
  }
  const grouped = promotions.reduce<IMergedPromotions>(callbackfn, {
    descriptions: [],
    promotionMap: new Map(),
  });
  if (
    [PaymentMethod.NAVER_PAY, undefined].some(paymentMethod =>
      grouped.promotionMap.get(paymentMethod),
    )
  ) {
    return [defaultNaverPayPromotion].reduce(callbackfn, grouped);
  }
  return grouped;
};

function getCardPromotions(fares: IFare[], passengers: IPassengers) {
  const merged: IMergedPromotions = {
    descriptions: [],
    promotionMap: new Map(),
  };
  if (
    fares.every(
      ({ discountFare }) =>
        discountFare &&
        discountFare.discountInfo &&
        isEqual(discountFare.discountInfo, fares[0].discountFare.discountInfo),
    )
  ) {
    const { cardType, discountType, discountRange, amount } =
      fares[0].discountFare.discountInfo;
    if (!(cardType && discountType && suffixMap.get(discountType) && amount)) {
      return merged;
    }
    const totalDiscount = fares.reduce((acc, fare) => {
      const {
        discountFare: { adultDiscountFare, childDiscountFare },
      } = fare;
      const discount =
        getTotalFare(fare, passengers) -
        (adultDiscountFare * passengers.adult +
          childDiscountFare * passengers.child);
      // if (!discount) return acc;
      return acc + discount;
    }, 0);
    const discountInfo = getNewDiscountInfo(amount, false, totalDiscount);
    const description = `${cardType.name} 결제 시 ${getDiscountStr([
      discountType,
      discountInfo,
    ])} 대상 항공편입니다. (${descriptionMap.get(discountRange)})`;
    merged.descriptions.push({ description, totalDiscount });
    merged.promotionMap.set(cardType.code, [
      cardType.name,
      new Map([[discountType, discountInfo]]),
    ]);
    return merged;
  }
  return fares.reduce((acc, fare) => {
    const { discountFare } = fare;
    if (!discountFare) return acc;
    const {
      adultDiscountFare,
      childDiscountFare,
      discountInfo: { cardType, discountType, discountRange, amount },
    } = discountFare;
    if (!(cardType && discountType && suffixMap.get(discountType) && amount)) {
      return acc;
    }
    const totalDiscount =
      getTotalFare(fare, passengers) -
      (adultDiscountFare * passengers.adult +
        childDiscountFare * passengers.child);
    // if (!totalDiscount) return acc;
    const description = `${cardType.name} 결제 시 ${getDiscountStr([
      discountType,
      getNewDiscountInfo(amount, false, totalDiscount),
    ])} 대상 항공편입니다. (${descriptionMap.get(discountRange)})`;
    acc.descriptions.push({ description, totalDiscount });
    const type = PromotionType.DEFAULT;
    const newDiscountInfo = getNewDiscountInfo(amount, true, totalDiscount);
    const found = acc.promotionMap.get(cardType.code);
    if (!found) {
      acc.promotionMap.set(cardType.code, [
        cardType.name,
        new Map([[type, newDiscountInfo]]),
      ]);
      return acc;
    }
    const [, promotions] = found;
    const discountInfo = promotions.get(type);
    if (!discountInfo) {
      promotions.set(type, newDiscountInfo);
      return acc;
    }
    discountInfo.totalDiscount += totalDiscount;
    if (isCombinedBenefit(discountInfo)) return acc;
    if (amount.unit === Unit.KRW) discountInfo[Unit.KRW] += totalDiscount;
    if (amount.unit === Unit.PERCENT) {
      discountInfo[Unit.PERCENT] = 0;
      discountInfo[Unit.KRW] = 0;
    }
    return acc;
  }, merged);
}

function getPromotions(
  nPayPromotions: IPromotion[],
  cardPromotions: IMergedPromotions,
  flights: DomesticItinerary[],
  otaId: string,
  supportNPay: boolean,
  fares: IFare[],
  passengers: IPassengers,
  originalPrice: number,
) {
  const { descriptions, promotionMap } = [
    getNPayPromotions(
      nPayPromotions,
      flights,
      otaId,
      supportNPay,
      fares,
      passengers,
    ),
    cardPromotions,
  ].reduce<IMergedPromotions>(
    (acc, cur) => {
      acc.descriptions.push(...cur.descriptions);
      cur.promotionMap.forEach((value, key) => {
        acc.promotionMap.set(key, value);
      });
      return acc;
    },
    { descriptions: [], promotionMap: new Map() },
  );
  const defaultBenefit = promotionMap
    .get(undefined)?.[1]
    .get(PromotionType.NAVER_PAY);

  if (defaultBenefit) {
    promotionMap.forEach(([, promotions], paymentMethod) => {
      if (!paymentMethod) return;
      const original = promotions.get(PromotionType.NAVER_PAY);
      if (!original) {
        promotions.set(PromotionType.NAVER_PAY, defaultBenefit);
        return;
      }
      original.totalDiscount += defaultBenefit.totalDiscount;
      if (isCombinedBenefit(defaultBenefit)) {
        original[Unit.PERCENT] = 0;
        original[Unit.KRW] = 0;
      } else {
        original[Unit.PERCENT] += defaultBenefit[Unit.PERCENT] || 0;
        original[Unit.KRW] += defaultBenefit[Unit.KRW] || 0;
      }
    });
  }

  return {
    promotions: convertPromotionMapToArray(
      promotionMap,
      originalPrice,
      otaId,
    ).sort((a, b) => a.discountedPrice - b.discountedPrice),
    descriptions: descriptions
      .sort((a, b) => b.totalDiscount - a.totalDiscount)
      .map(({ description }) => description),
  };
}

export { getPromotions as default, getCardPromotions };
